import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ProductCard from "../../components/ProductCard";
import { URUN_DOSYALARI, kategoriAdi, useKatalog } from "../../data/katalog";
import { markaYolu } from "../../data/markalar";

// Ana sayfada kartı çıkacak markalar (sıra korunur, kataloğunda ürün olmayan atlanır)
const ONE_CIKAN_MARKALAR = [
  "Filli Boya",
  "Polisan",
  "Marshall",
  "Kalekim",
  "Dekor",
  "VitrA",
  "ECA",
  "DeWalt",
  "İzeltaş",
  "Soudal",
  "Bosch",
  "Viko",
];

const YENI_URUN_SAYISI = 8;

/** Öne çıkan markalar: marka adı, ürün sayısı ve üç ürün görseli; tıklanınca markanın kataloğu açılır. */
export function OneCikanMarkalar() {
  const { t } = useTranslation();
  const { urunler, hazir } = useKatalog();

  const markalar = useMemo(() => {
    const grup = new Map();
    for (const u of urunler) {
      if (!grup.has(u.marka)) grup.set(u.marka, []);
      grup.get(u.marka).push(u);
    }
    return ONE_CIKAN_MARKALAR.filter((m) => grup.has(m)).map((m) => {
      const liste = grup.get(m);
      // Görseller farklı kategorilerden seçilsin ki kart markanın genişliğini göstersin
      const secilen = [];
      const kategoriler = new Set();
      for (const u of liste) {
        if (secilen.length === 3) break;
        if (kategoriler.has(u.kategori)) continue;
        kategoriler.add(u.kategori);
        secilen.push(u);
      }
      for (const u of liste) {
        if (secilen.length === 3) break;
        if (!secilen.includes(u)) secilen.push(u);
      }
      return { ad: m, adet: liste.length, gorseller: secilen.map((u) => u.gorsel) };
    });
  }, [urunler]);

  if (hazir && markalar.length === 0) return null;

  return (
    <section className="border-b border-line bg-canvas/60">
      <div className="container-page py-14 sm:py-16">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow">{t("home.featuredBrandsEyebrow")}</p>
            <h2 className="mt-3 text-[24px] font-semibold leading-tight tracking-[-0.02em] text-ink sm:text-[28px]">
              {t("home.featuredBrandsTitle")}
            </h2>
          </div>
          <Link to="/markalar" className="link-arrow text-[13px]">
            {t("brandPage.allBrands")}
            <span aria-hidden="true" className="arrow-forward" />
          </Link>
        </div>

        <ul className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4">
          {(hazir ? markalar : ONE_CIKAN_MARKALAR.slice(0, 8).map((ad) => ({ ad, adet: 0, gorseller: [] }))).map(
            (m) => (
              <li key={m.ad}>
                <Link
                  to={markaYolu(m.ad)}
                  className="group flex h-full flex-col overflow-hidden rounded-xl border border-line bg-white transition-all duration-300 hover:-translate-y-1 hover:border-transparent hover:shadow-[0_14px_34px_-12px_rgba(0,0,0,0.22)]"
                >
                  {/* grid-rows-1 + min-h-0: uzun (dikey) ürün görselleri hücreyi büyütüp aşağı taşmasın */}
                  <div className="grid h-24 grid-cols-3 grid-rows-1 gap-px overflow-hidden bg-line/60 sm:h-28">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="relative min-h-0 overflow-hidden bg-white">
                        {m.gorseller[i] ? (
                          <img
                            src={m.gorseller[i]}
                            alt=""
                            loading="lazy"
                            className="absolute inset-0 h-full w-full object-contain p-2 transition-transform duration-500 group-hover:scale-110"
                          />
                        ) : (
                          <div className="h-full w-full animate-pulse rounded bg-canvas" />
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-1 items-center justify-between gap-2 border-t border-line px-4 py-3.5">
                    <div className="min-w-0">
                      <p className="truncate text-[15px] font-bold uppercase tracking-wide text-ink">
                        {m.ad.toLocaleUpperCase("tr")}
                      </p>
                      {m.adet > 0 && (
                        <p className="mt-0.5 text-[12px] tabular-nums text-muted">
                          {t("home.brandProductCount", { count: m.adet })}
                        </p>
                      )}
                    </div>
                    <span
                      aria-hidden="true"
                      className="arrow-forward flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-canvas text-ink transition-colors duration-300 group-hover:bg-ink group-hover:text-white"
                    />
                  </div>
                </Link>
              </li>
            )
          )}
        </ul>
      </div>
    </section>
  );
}

/** Yeni eklenenler: kataloğa en son eklenen dosyalardan sırayla birer ürün. */
export function YeniEklenenler() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "tr").split("-")[0];
  const { urunler, adlar, hazir } = useKatalog();

  const yeniler = useMemo(() => {
    const dosyaSirasi = [...URUN_DOSYALARI].reverse();
    const gruplar = dosyaSirasi.map((d) => urunler.filter((u) => u._dosya === d));
    const secilen = [];
    // Her yeni dosyadan sırayla birer ürün: tek marka listeyi doldurmasın
    for (let tur = 0; secilen.length < YENI_URUN_SAYISI && tur < 5; tur++) {
      for (const g of gruplar) {
        // Aynı dosyadan farklı kategorilerden seç
        const aday = g.filter((u) => !secilen.some((s) => s.kategori === u.kategori))[0] || g[tur];
        if (aday && !secilen.includes(aday)) secilen.push(aday);
        if (secilen.length === YENI_URUN_SAYISI) break;
      }
    }
    return secilen;
  }, [urunler]);

  if (!hazir || yeniler.length === 0) return null;

  return (
    <section className="border-t border-line">
      <div className="container-page py-14 sm:py-16">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow">{t("home.newArrivalsEyebrow")}</p>
            <h2 className="mt-3 text-[24px] font-semibold leading-tight tracking-[-0.02em] text-ink sm:text-[28px]">
              {t("home.newArrivalsTitle")}
            </h2>
          </div>
          <Link to="/urunler" className="link-arrow text-[13px]">
            {t("home.seeCatalog")}
            <span aria-hidden="true" className="arrow-forward" />
          </Link>
        </div>

        {/* Mobilde yana kaydırılır, masaüstünde 4 sütun */}
        <div className="scrollbar-hide -mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-5 sm:overflow-visible sm:px-0 lg:grid-cols-4">
          {yeniler.map((u) => (
            <div key={u.id} className="w-[75%] shrink-0 snap-start sm:w-auto">
              <ProductCard urun={u} altBilgi={kategoriAdi(adlar, u.kategori, lang)} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

